/**
 * Typed client for the HORIZONLOOK Worker control-plane API.
 *
 * Contract: docs/SPEC.md §3 (Worker API).
 *
 * Auth model: every browser request is gated by Cloudflare Access. Access sets
 * an httpOnly cookie for the SSO session that is sent automatically on
 * same-site/SSO requests, so the client simply uses `credentials: 'include'`.
 * There is NO login UI — Access redirects unauthenticated users before the SPA
 * ever loads. A 401/403 from the API therefore means the Access session lapsed
 * (reload to re-auth) or the user lacks the required role.
 */

export type DeviceStatus = "pending" | "active" | "disabled";
export type DeviceMode = "idle" | "live" | "capture" | "decode";
export type SdrType = "rtlsdr" | "hackrf" | "unknown";
export type CaptureStatus = "pending" | "stored" | "expired" | "capturing";
export type SigmfDatatype = "cu8" | "ci8" | "ci16";
export type UserRole = "admin" | "user";

/** A device row as returned by GET /devices and GET /devices/:id. SPEC §3.2. */
export interface Device {
  id: string;
  name: string;
  location: string | null;
  sdr_type: SdrType | null;
  status: DeviceStatus;
  hostname: string | null;
  mode: DeviceMode | null;
  disk_free: number | null; // bytes
  temp_c: number | null;
  agent_version: string | null;
  owrx_version: string | null;
  cpu_percent: number | null; // overall CPU utilisation %
  last_seen: string | null; // UTC ISO-8601
  // Recovery mode (SPEC §3.9).
  recovery_active?: boolean; // device is phoning home from recovery
  recovery_reason?: string | null;
  recovery_staged?: boolean; // an operator has staged a recovery script
  recovery_last?: string | null; // last check-in/result summary
}

/** A capture row. SPEC §2 (captures table) / §3.3. */
export interface Capture {
  id: string;
  device_id: string;
  name: string;
  freq: number; // Hz
  rate: number; // sps
  datatype: SigmfDatatype;
  duration_s: number;
  bytes: number; // compressed size in R2
  status: CaptureStatus;
  started_utc: string;
  created_at: string;
  expires_at: string | null;
}

/** GET /devices/:id returns the device plus its recent captures. */
export interface DeviceDetail extends Device {
  captures: Capture[];
}

/** Live agent status, brokered via GET /devices/:id/mode (SPEC §3.3 / §4). */
export interface AgentStatus {
  mode: DeviceMode;
  sdr_type: SdrType;
  owrx_version: string;
  agent_version: string;
  disk_free: number;
  temp_c: number;
  cpu_percent?: number | null;
  uptime_s: number;
  capture?: {
    capture_id: string;
    freq: number;
    rate: number;
    duration_s: number;
    elapsed_s?: number;
  };
  decode?: DecodeStatus;
  sdrs?: SdrSlot[];
  /** Per-format decoder readiness on this device (binaries present). */
  decoders?: Record<string, boolean>;
  /** Per-format supported SDR types (e.g. {"adsb":["rtlsdr"]}); UI greys others. */
  decoder_sdrs?: Record<string, string[]>;
}

/** An installable optional decoder package (GET /devices/:id/decoders). */
export interface DecoderPackage {
  name: string;
  title: string;
  formats: string[];
  binaries: string[];
  size_mb: number;
  note: string;
  installed: boolean;
}

/** Decoder-package inventory + disk + in-flight install/remove action. */
export interface DecodersInfo {
  disk: { free: number; total: number };
  packages: DecoderPackage[];
  action: {
    running: boolean;
    op?: string | null;
    package?: string | null;
    started_utc?: string | null;
    log_tail?: string;
  };
}

/** One SDR slot and its current task (SPEC §10). */
export interface SdrSlot {
  id: string;
  index: number;
  port: string; // physical USB port path (stable identity), or idx<N>
  type: string;
  label: string;
  task: "idle" | "live" | "capture" | "decode";
  detail?: {
    format?: string | null;
    freq?: number | null;
    count?: number;
    last_label?: string | null;
    capture_id?: string;
    rate?: number;
    duration_s?: number;
    started_utc?: string;
  } | null;
}

/** Background-decoder status (SPEC §9), part of AgentStatus. */
export interface DecodeStatus {
  running: boolean;
  format: string | null;
  freq: number | null;
  device: string | null;
  started_utc: string | null;
  count: number;
  last_label: string | null;
  last_ts: string | null;
}

/** A decoded-signal record (GET /decodes, SPEC §9). */
export interface Decode {
  id: number;
  device_id: string;
  format: string; // the decoder (envelope: decoder)
  protocol: string | null; // next-layer type, e.g. "iridium.paging" (envelope: nextlayer)
  freq: number | null;
  ts: string;
  label: string | null;
  body: string;
  created_at: string;
  tail: string | null; // aircraft registration (ACARS/VDL2)
  flight: string | null; // flight number
  mlabel: string | null; // protocol message label
}

/** Phase-B fusion: per-target entity profiles (GET /entities). */
export interface EntityLocation { lat: number; lon: number; radius_km: number | null; ts: string; }
export interface TerminalEntity {
  type: "terminal"; via: "identity" | "call"; id: string; imsi?: string; imei?: string;
  tmsis: string[]; msisdns: string[]; sensors: string[];
  observations: number; first_seen: string; last_seen: string;
  location: EntityLocation | null;
}
export interface AircraftEntity {
  type: "aircraft"; id: string; tail: string; observations: number;
  first_seen: string; last_seen: string; sensors: number; recent: string | null;
}
export interface TmsiEntity {
  type: "tmsi"; id: string; tmsi: string;
  location: EntityLocation; sensors: string[]; last_seen: string;
}
export interface EntitiesResponse {
  terminals: TerminalEntity[]; aircraft: AircraftEntity[]; located: TmsiEntity[];
  meta: { terminals: number; terminals_via_call?: number; aircraft: number; located: number; geolocated_tmsis: number };
}

/** One target's full history (GET /entity) — chronological records + position track. */
export interface TimelineRecord {
  ts: string; protocol: string | null; format: string; label: string | null;
  lat: number | null; lon: number | null; radius_km: number | null;
  device_id: string; text: string | null;
}
export interface EntityTimeline {
  id: string; count: number; first_seen: string | null; last_seen: string | null;
  sensors: string[];
  track: { ts: string; lat: number; lon: number; radius_km: number | null }[];
  records: TimelineRecord[];
}

/** A located target (GET /locations) — latest position per target, any source. */
export interface Location {
  id: number;
  device_id: string;
  format: string; // decoder
  protocol: string | null; // source/type, e.g. "iridium.geoloc", "adsb"
  lat: number;
  lon: number;
  radius_km: number | null; // estimate error radius (nullable; e.g. geoloc)
  ident: string | null; // target id (tmsi/icao/…)
  label: string | null;
  ts: string;
  body: string;
}

/** A saved channel preset (known-good decoder values). GET/POST /presets. */
export interface Preset {
  id: number;
  protocol: string; // grouping key (format minus -multi/-survey)
  format: string | null; // optional target decoder format
  label: string;
  freq: number | null; // single-channel Hz
  channels: number[] | null; // one or more channels, Hz
  extra: Record<string, unknown> | null; // arfcn/band/mcc/mnc/lac/cid/power…
  source: string | null; // 'survey' | 'manual'
  device_id: string | null;
  created_at: string;
}

/** Base protocol a decoder format belongs to (groups presets across single/multi/survey). */
export function protocolForFormat(fmt: string): string {
  return fmt.replace(/-(survey|multi)$/, "");
}

export interface User {
  email: string;
  role: UserRole;
  created_at: string;
}

/**
 * Current authenticated principal.
 *
 * NOTE TO WORKER: SPEC §3 does not define `GET /me`. The UI needs the caller's
 * email + role to gate admin-only views client-side (the Worker still enforces
 * server-side — this is purely for UX, hiding controls the user can't use).
 * Until `GET /me` exists, `getMe()` falls back to probing an admin-only GET
 * (GET /users) and inferring role from the HTTP status (200 => admin,
 * 403 => user). Please add `GET /me -> { email, role }`.
 */
export interface Me {
  email: string;
  role: UserRole;
}

/**
 * Latest published update release (GET /updates/latest, SPEC §3.6). `version` is
 * null when no bundle has been published yet. The UI compares it against each
 * device's reported `agent_version` to flag out-of-date devices.
 */
export interface LatestUpdate {
  version: string | null;
  sha256?: string;
  size?: number | null;
  created_utc?: string | null;
}

export interface ApiErrorBody {
  error: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
  get isAuth(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

const API_BASE = (
  import.meta.env.VITE_API_BASE ?? "https://api.sdr.vladscoffee.com"
).replace(/\/+$/, "");

/** Origin that serves the Worker `/live/:words/*` proxy route (SPEC §3.7). */
export const LIVE_BASE = (
  import.meta.env.VITE_LIVE_BASE ?? window.location.origin
).replace(/\/+$/, "");

async function request<T>(
  path: string,
  init?: RequestInit & { rawStatusOk?: number[] },
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(init?.headers ?? {}),
      },
      ...init,
    });
  } catch (e) {
    throw new ApiError(0, "network", `Network error: ${(e as Error).message}`);
  }

  if (!res.ok) {
    let body: Partial<ApiErrorBody> = {};
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      body.error ?? `http_${res.status}`,
      body.message ?? res.statusText ?? `Request failed (${res.status})`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Build an absolute URL to the in-page Live view for a device hostname slug. */
export function liveUrl(hostname: string): string {
  // hostname is e.g. `hl-amber-quiet-harbor.sdr.vladscoffee.com`; the proxy is
  // keyed by the slug words. SPEC §3.7: /live/:words/*.
  const slug = hostname.split(".")[0].replace(/^hl-/, "");
  return `${LIVE_BASE}/live/${slug}/`;
}

export const api = {
  // ---- current user ------------------------------------------------------
  /**
   * Resolve the current principal. Prefers `GET /me`; falls back to probing an
   * admin-only endpoint when /me is not yet implemented by the Worker.
   */
  async getMe(): Promise<Me> {
    try {
      return await request<Me>("/me");
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // /me not implemented — infer role from an admin-only GET.
        try {
          await request<User[]>("/users");
          return { email: "", role: "admin" };
        } catch (probe) {
          if (probe instanceof ApiError && probe.status === 403) {
            return { email: "", role: "user" };
          }
          throw probe;
        }
      }
      throw e;
    }
  },

  // ---- devices -----------------------------------------------------------
  listDevices(): Promise<Device[]> {
    return request<Device[]>("/devices");
  },
  getDevice(id: string): Promise<DeviceDetail> {
    return request<DeviceDetail>(`/devices/${encodeURIComponent(id)}`);
  },
  approveDevice(id: string): Promise<Device> {
    return request<Device>(`/devices/${encodeURIComponent(id)}/approve`, {
      method: "POST",
    });
  },
  updateDevice(
    id: string,
    patch: { name?: string; location?: string },
  ): Promise<Device> {
    return request<Device>(`/devices/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  disableDevice(id: string): Promise<Device> {
    return request<Device>(`/devices/${encodeURIComponent(id)}/disable`, {
      method: "POST",
    });
  },
  deleteDevice(id: string): Promise<void> {
    return request<void>(`/devices/${encodeURIComponent(id)}/delete`, {
      method: "POST",
    });
  },
  rebootDevice(id: string): Promise<void> {
    return request<void>(`/devices/${encodeURIComponent(id)}/reboot`, {
      method: "POST",
    });
  },
  updateAgent(id: string): Promise<void> {
    return request<void>(`/devices/${encodeURIComponent(id)}/update`, {
      method: "POST",
    });
  },

  // ---- decoders (SPEC §9) ------------------------------------------------
  startDecode(
    id: string,
    body: { format?: string; freq?: number },
  ): Promise<{ status: string }> {
    return request<{ status: string }>(`/devices/${encodeURIComponent(id)}/decode`, {
      method: "POST",
      body: JSON.stringify({ action: "start", ...body }),
    });
  },
  stopDecode(id: string): Promise<{ status: string }> {
    return request<{ status: string }>(`/devices/${encodeURIComponent(id)}/decode`, {
      method: "POST",
      body: JSON.stringify({ action: "stop" }),
    });
  },
  listDecodes(params?: {
    device_id?: string;
    format?: string;
    protocol?: string;
    q?: string;
    since?: string;
    tail?: string;
    flight?: string;
    limit?: number;
  }): Promise<Decode[]> {
    const qs = new URLSearchParams();
    if (params?.device_id) qs.set("device_id", params.device_id);
    if (params?.format) qs.set("format", params.format);
    if (params?.protocol) qs.set("protocol", params.protocol);
    if (params?.q) qs.set("q", params.q);
    if (params?.since) qs.set("since", params.since);
    if (params?.tail) qs.set("tail", params.tail);
    if (params?.flight) qs.set("flight", params.flight);
    if (params?.limit) qs.set("limit", String(params.limit));
    const s = qs.toString();
    return request<Decode[]>(`/decodes${s ? `?${s}` : ""}`);
  },
  listEntities(limit?: number): Promise<EntitiesResponse> {
    return request<EntitiesResponse>(`/entities${limit ? `?limit=${limit}` : ""}`);
  },
  getEntity(id: string, limit?: number): Promise<EntityTimeline> {
    const qs = new URLSearchParams({ id });
    if (limit) qs.set("limit", String(limit));
    return request<EntityTimeline>(`/entity?${qs.toString()}`);
  },
  listLocations(params?: {
    protocol?: string;
    device_id?: string;
    since?: string;
    limit?: number;
  }): Promise<Location[]> {
    const qs = new URLSearchParams();
    if (params?.protocol) qs.set("protocol", params.protocol);
    if (params?.device_id) qs.set("device_id", params.device_id);
    if (params?.since) qs.set("since", params.since);
    if (params?.limit) qs.set("limit", String(params.limit));
    const s = qs.toString();
    return request<Location[]>(`/locations${s ? `?${s}` : ""}`);
  },

  // ---- channel presets (known-good decoder values) ----------------------
  listPresets(protocol?: string): Promise<Preset[]> {
    return request<Preset[]>(`/presets${protocol ? `?protocol=${encodeURIComponent(protocol)}` : ""}`);
  },
  createPreset(body: {
    protocol: string;
    format?: string;
    label: string;
    freq?: number;
    channels?: number[];
    extra?: unknown;
    source?: string;
    device_id?: string;
  }): Promise<{ id: number }> {
    return request<{ id: number }>("/presets", { method: "POST", body: JSON.stringify(body) });
  },
  deletePreset(id: number): Promise<void> {
    return request<void>(`/presets/${id}`, { method: "DELETE" });
  },

  // ---- optional decoder packages (web-managed install/remove) -----------
  getDecoders(id: string): Promise<DecodersInfo> {
    return request<DecodersInfo>(`/devices/${encodeURIComponent(id)}/decoders`);
  },
  manageDecoders(
    id: string,
    action: "install" | "remove",
    pkg: string,
  ): Promise<{ status: string; package: string }> {
    return request<{ status: string; package: string }>(
      `/devices/${encodeURIComponent(id)}/decoders`,
      { method: "POST", body: JSON.stringify({ action, package: pkg }) },
    );
  },

  // ---- per-SDR task assignment (SPEC §10) --------------------------------
  setSdrTask(
    id: string,
    sdrId: string,
    body: { task: string; freq?: number; rate?: number; duration_s?: number; gain?: number; bb_filter?: number; format?: string; channels?: number[] },
  ): Promise<{ id: string; task: string }> {
    return request<{ id: string; task: string }>(
      `/devices/${encodeURIComponent(id)}/sdr/${encodeURIComponent(sdrId)}/task`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  // ---- updates (latest published release) --------------------------------
  getLatestUpdate(): Promise<LatestUpdate> {
    return request<LatestUpdate>("/updates/latest");
  },

  // ---- recovery (admin: stage/clear an operator recovery script) ---------
  stageRecovery(
    id: string,
    body: { script_key: string; sha256: string },
  ): Promise<{ staged: boolean; action_id?: string }> {
    return request<{ staged: boolean; action_id?: string }>(
      `/devices/${encodeURIComponent(id)}/recovery`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  clearRecovery(id: string): Promise<{ staged: boolean; cleared?: boolean }> {
    return request<{ staged: boolean; cleared?: boolean }>(
      `/devices/${encodeURIComponent(id)}/recovery`,
      { method: "POST", body: JSON.stringify({ clear: true }) },
    );
  },

  // ---- mode & capture ----------------------------------------------------
  getMode(id: string): Promise<AgentStatus> {
    return request<AgentStatus>(`/devices/${encodeURIComponent(id)}/mode`);
  },
  setMode(id: string, mode: "idle" | "live"): Promise<AgentStatus> {
    return request<AgentStatus>(`/devices/${encodeURIComponent(id)}/mode`, {
      method: "POST",
      body: JSON.stringify({ mode }),
    });
  },
  startCapture(
    id: string,
    body: { freq: number; rate: number; duration_s: number },
  ): Promise<{ capture_id: string; status: string }> {
    return request<{ capture_id: string; status: string }>(
      `/devices/${encodeURIComponent(id)}/capture`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  listCaptures(id: string): Promise<Capture[]> {
    return request<Capture[]>(`/devices/${encodeURIComponent(id)}/captures`);
  },
  stopCapture(id: string, cid: string): Promise<void> {
    return request<void>(
      `/devices/${encodeURIComponent(id)}/capture/${encodeURIComponent(cid)}/stop`,
      { method: "POST" },
    );
  },

  // ---- downloads ---------------------------------------------------------
  getDownload(
    cid: string,
  ): Promise<{ data_url: string; meta_url: string; expires_at: string }> {
    return request<{ data_url: string; meta_url: string; expires_at: string }>(
      `/captures/${encodeURIComponent(cid)}/download`,
    );
  },

  // ---- users (admin) -----------------------------------------------------
  listUsers(): Promise<User[]> {
    return request<User[]>("/users");
  },
  addUser(email: string, role: UserRole): Promise<User> {
    return request<User>("/users", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    });
  },
  setUserRole(email: string, role: UserRole): Promise<User> {
    return request<User>(`/users/${encodeURIComponent(email)}`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    });
  },
  removeUser(email: string): Promise<void> {
    return request<void>(`/users/${encodeURIComponent(email)}`, {
      method: "DELETE",
    });
  },
};

// ---- capture sizing helpers (SPEC §7) ------------------------------------

/** Bytes per IQ sample for each SigMF datatype. SPEC §7. */
export const BYTES_PER_SAMPLE: Record<SigmfDatatype, number> = {
  cu8: 2,
  ci8: 2,
  ci16: 4,
};

/** The SigMF datatype a given SDR produces. SPEC §6. */
export function datatypeForSdr(sdr: SdrType | null): SigmfDatatype {
  return sdr === "hackrf" ? "ci8" : "cu8";
}

/** Projected raw IQ size in bytes for a capture. SPEC §7. */
export function projectedBytes(
  rate: number,
  durationS: number,
  datatype: SigmfDatatype,
): number {
  return Math.round(rate * BYTES_PER_SAMPLE[datatype] * durationS);
}

/** Per-SDR caps (SPEC §7). */
export const SDR_CAPS: Record<
  SdrType,
  { maxRate: number; maxDuration: number }
> = {
  rtlsdr: { maxRate: 2_400_000, maxDuration: 3600 },
  hackrf: { maxRate: 8_000_000, maxDuration: 600 },
  unknown: { maxRate: 2_400_000, maxDuration: 600 },
};

/** 1 GiB headroom reserved before allowing a capture. SPEC §7. */
export const DISK_HEADROOM_BYTES = 1024 * 1024 * 1024;

// ---- frequency unit helpers (shared by capture + decoder + SDR cards) -----
export const FREQ_UNITS = [
  { label: "Hz", value: "Hz" },
  { label: "kHz", value: "kHz" },
  { label: "MHz", value: "MHz" },
  { label: "GHz", value: "GHz" },
];
export const FREQ_UNIT_HZ: Record<string, number> = { Hz: 1, kHz: 1e3, MHz: 1e6, GHz: 1e9 };

/** Split an Hz value into a {value, unit} pair for the number+unit inputs. */
export function hzToValueUnit(hz: number): { value: string; unit: string } {
  if (hz >= 1e9) return { value: String(hz / 1e9), unit: "GHz" };
  if (hz >= 1e6) return { value: String(hz / 1e6), unit: "MHz" };
  if (hz >= 1e3) return { value: String(hz / 1e3), unit: "kHz" };
  return { value: String(hz), unit: "Hz" };
}

// ---- decoder formats (SPEC §9) -------------------------------------------
/**
 * The decoder profiles the agent supports. `defaultHz` pre-fills the frequency
 * when a format is picked; `needsBuild` marks decoders whose binary isn't in apt
 * (installed via install-decoders.sh) so the UI can hint at it.
 */
export interface DecoderFormat {
  value: string;
  label: string;
  defaultHz: number;
  needsBuild?: boolean;
  multi?: boolean; // accepts one OR more frequencies (channelizes when given >1)
  defaultChannels?: string; // default comma-separated MHz list (one or more)
  note?: string;
}
export const DECODER_FORMATS: DecoderFormat[] = [
  { value: "rtl_433", label: "ISM sensors (rtl_433)", defaultHz: 433_920_000 },
  { value: "pocsag", label: "Paging POCSAG/FLEX", defaultHz: 153_350_000, multi: true, defaultChannels: "153.350", note: "one or more channels (region-specific)" },
  { value: "adsb", label: "Aircraft ADS-B (1090, RTL)", defaultHz: 1_090_000_000, needsBuild: true },
  { value: "ais", label: "Marine AIS (162)", defaultHz: 162_000_000, needsBuild: true },
  { value: "acars", label: "Aircraft ACARS (131)", defaultHz: 131_725_000, multi: true, needsBuild: true, defaultChannels: "131.725", note: "one or more channels (EU primary 131.725)" },
  { value: "vdl2", label: "Aircraft VDL Mode 2 (136.7–137.0)", defaultHz: 136_975_000, multi: true, needsBuild: true, defaultChannels: "136.725, 136.775, 136.875, 136.975", note: "VHF Data Link Mode 2" },
  { value: "dmr", label: "DMR metadata", defaultHz: 446_000_000, needsBuild: true },
  { value: "tetra", label: "TETRA + SDS text", defaultHz: 425_612_500, needsBuild: true, note: "GNU Radio PHY; SDS-TL text + call setups" },
  { value: "gsm", label: "GSM paging/cell (live)", defaultHz: 948_086_000, needsBuild: true, note: "gr-gsm; paging IMSI + cell ID, metadata only. Set freq to a known ARFCN" },
  { value: "iridium", label: "Iridium L-band (sat)", defaultHz: 1_625_000_000, needsBuild: true, note: "HackRF + active antenna; streams paging (TMSI/MSC + sat position), SBD messages, and IDA access layer (IMSI/IMEI/MSISDN)" },
  { value: "iridium-acars", label: "Iridium ACARS (sat)", defaultHz: 1_625_000_000, needsBuild: true, note: "HackRF + active antenna; ACARS-over-Iridium (ADS-C/CPDLC). Mutually exclusive with 'iridium' — sniffer's ACARS mode suppresses the raw stream" },
  { value: "inmarsat-c", label: "Inmarsat-C / STD-C (sat)", defaultHz: 1_541_450_000, needsBuild: true, note: "L-band EGC/marine safety + messages. Needs an antenna aimed at the Inmarsat GEO bird (south) — not the Iridium whole-sky antenna. UNVALIDATED until first live lock" },
  { value: "spectrum-survey", label: "Spectrum occupancy map (fast)", defaultHz: 100_000_000, note: "wideband sweep — classifies all carriers (the quick 'what's here' first pass); freq ignored" },
  { value: "gsm-survey", label: "GSM/GSM-R site survey", defaultHz: 922_000_000, needsBuild: true, note: "kal scans bands + grgsm characterises each; slow, freq ignored" },
  { value: "pocsag-survey", label: "POCSAG/FLEX site survey", defaultHz: 153_350_000, needsBuild: true, note: "scans regional pager band plan; slow, freq ignored" },
  { value: "dmr-survey", label: "DMR site survey", defaultHz: 446_000_000, needsBuild: true, note: "scans regional DMR/PMR band plan; slow, freq ignored" },
  { value: "dv-survey", label: "Digital-voice survey (all modes)", defaultHz: 446_000_000, needsBuild: true, note: "dsd-fme auto: DMR/P25/NXDN/dPMR/YSF/D-STAR/M17; freq ignored" },
  { value: "tetra-survey", label: "TETRA site survey", defaultHz: 425_612_500, needsBuild: true, note: "scans TETRA BOS+commercial band plan; slow, freq ignored" },
  { value: "tetrapol-survey", label: "TETRAPOL/POLYCOM site survey", defaultHz: 393_000_000, needsBuild: true, note: "scans 380-400/410-430; GNU Radio demod + tetrapol_dump; slow, freq ignored" },
  { value: "lte-survey", label: "LTE site survey", defaultHz: 806_000_000, needsBuild: true, note: "CellSearch over regional LTE bands → PCIs; freq ignored" },
  { value: "navtex", label: "NAVTEX (518 kHz)", defaultHz: 518_000, needsBuild: true, note: "no decoder binary yet" },
];
